import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import { getFriendlyDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { hasTokenPattern } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/tokens'
import {
  convertHTMLParagraphBreaks,
  convertHtmlSpace,
  parseHtmlTags,
} from '@lyrahealth-inc/shared-app-logic/src/features/selfCareMedia/utils'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { DocumentSearchIcon } from '../../atoms/icons/DocumentSearchIcon'
import { EditIcon } from '../../atoms/icons/EditIcon'
import { SendIcon } from '../../atoms/icons/SendIcon'
import { TrashIcon } from '../../atoms/icons/TrashIcon'
import { LoadingSkeleton } from '../../atoms/loadingSkeleton/LoadingSkeleton'
import { TokenTextInput } from '../../atoms/tokenTextInput/TokenTextInput.web'
import { HoverActionContainer } from '../../organisms/hoverActionContainer/HoverActionContainer'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { HoverActionItem } from '../hoverActions/HoverActions'
import { ToggleSwitch } from '../toggleSwitch/ToggleSwitch'

export type MessengerUtilityItemProps = {
  header?: string
  loading?: boolean
  id: string
  onDeletePress?: () => void
  onEditPress?: () => void
  onPress?: () => void
  onSendPress?: () => void
  onOpenOriginalFormPress?: () => void
  onToggleSwitch?: (value: boolean) => void
  subheader?: string
  text: string
  topBorder?: boolean
  useHoverActionButtons?: boolean
  useTokenTextInput?: boolean
  updateDate?: string
  useToggleSwitch?: boolean
  isExpanded?: boolean
  expandedContent?: React.ReactNode
}

const MessengerUtilityItemButton = styled(HoverActionContainer)(({ theme }) => ({
  padding: `0px ${theme.spacing['16px']}`,
}))

const InnerContainer = styled.View<{ topBorder: boolean }>(({ theme, topBorder }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing['8px'],
  padding: `${theme.spacing['16px']} 0px`,
  ...(topBorder && { borderTopWidth: '1px', borderTopColor: theme.colors.borderDefault }),
}))

const TitleContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
})

const TitleTextContainer = styled.View({
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
})

const LoadingTitleTextContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['4px'],
}))

const TextContainer = styled.View<{ isExpanded?: boolean }>(({ isExpanded }) => ({
  width: '100%',
  textAlign: 'left',
  height: 'auto',
  maxHeight: isExpanded ? 'auto' : '100px',
  overflow: isExpanded ? 'visible' : 'hidden',
}))

const BodyTextContainer = styled.View({
  height: 'auto',
})

const ToggleContainer = styled.View({
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
})

const ExpandedContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.spacing['12px'],
  paddingHorizontal: theme.spacing['4px'],
  paddingBottom: theme.spacing['16px'],
  width: '100%',
}))

export const MessengerUtilityItem = ({
  header,
  id,
  loading,
  onDeletePress,
  onEditPress,
  onPress,
  onSendPress,
  onToggleSwitch,
  subheader,
  text,
  topBorder = false,
  updateDate,
  useHoverActionButtons,
  useTokenTextInput = false,
  useToggleSwitch,
  onOpenOriginalFormPress,
  isExpanded = false,
  expandedContent,
}: MessengerUtilityItemProps) => {
  const { colors } = useTheme() as ThemeType
  const intl = useIntl()
  const { formatMessage } = intl
  const [isHovered, setIsHovered] = useState(false)
  const [isToggleSwitchOn, setIsToggleSwitchOn] = useState(false)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/Los_Angeles'

  const hasRichTextTokens = hasTokenPattern(text)
  const utilityItems = useMemo<HoverActionItem[]>(() => {
    const items: HoverActionItem[] = []

    if (onOpenOriginalFormPress) {
      items.push({
        id: 'openOriginalForm',
        onPress: onOpenOriginalFormPress,
        icon: <DocumentSearchIcon fillColor={colors.iconDefault} size={24} />,
        label: formatMessage({
          defaultMessage: 'Open original form',
          description: 'Label for open original form button',
        }),
        testId: tID('MessengerUtilityItemActionButtons-openOriginalForm'),
      })
    }

    if (onDeletePress) {
      items.push({
        id: 'delete',
        onPress: onDeletePress,
        icon: <TrashIcon fillColor={colors.iconDefault} size={24} />,
        label: formatMessage({
          defaultMessage: 'Delete',
          description: 'Label for delete button',
        }),
        testId: tID('MessengerUtilityItemActionButtons-delete'),
      })
    }
    if (onEditPress) {
      items.push({
        id: 'edit',
        onPress: onEditPress,
        icon: <EditIcon fillColor={colors.iconDefault} size={24} isFilled={false} />,
        label: formatMessage({
          defaultMessage: 'Edit',
          description: 'Label for edit button',
        }),
        testId: tID('MessengerUtilityItemActionButtons-edit'),
      })
    }
    if (onSendPress) {
      items.push({
        id: 'send',
        onPress: onSendPress,
        icon: <SendIcon fillColor={colors.iconDefault} size={24} />,
        label: formatMessage({
          defaultMessage: 'Send',
          description: 'Label for send button',
        }),
        testId: tID('MessengerUtilityItemActionButtons-send'),
      })
    }
    return items
  }, [onOpenOriginalFormPress, onDeletePress, onEditPress, onSendPress, colors.iconDefault, formatMessage])
  return (
    <MessengerUtilityItemButton
      testID={tID(`MessengerUtilityItemButton-${id}`)}
      onPress={onPress}
      useHoveredBackground={isExpanded}
      items={utilityItems}
      isHovered={isHovered}
      setIsHovered={setIsHovered}
      disableHoverActions={!useHoverActionButtons}
    >
      {loading ? (
        <InnerContainer testID={tID('MessengerUtilityItem-innerContainer')} topBorder={topBorder}>
          <TitleContainer testID={tID('MessengerUtilityItem-titleContainer')}>
            <LoadingTitleTextContainer testID={tID('MessengerUtilityItem-titleTextContainer')}>
              <LoadingSkeleton width={150} height={20} />
              <LoadingSkeleton width={250} height={20} />
            </LoadingTitleTextContainer>
            <LoadingSkeleton width={120} height={40} />
          </TitleContainer>
          <TextContainer testID={tID('MessengerUtilityItem-textContainer')}>
            <LoadingSkeleton width={'100%'} height={45} />
          </TextContainer>
        </InnerContainer>
      ) : (
        <InnerContainer testID={tID('MessengerUtilityItem-innerContainer')} topBorder={topBorder}>
          <TitleContainer testID={tID('MessengerUtilityItem-titleContainer')}>
            <TitleTextContainer testID={tID('MessengerUtilityItem-titleTextContainer')}>
              {header && (
                <BodyText testID={tID('MessengerUtilityItem-header')} text={header} size={BodyTextSize.DEFAULT} />
              )}
              {subheader && (
                <BodyText
                  testID={tID('MessengerUtilityItem-subheader')}
                  text={subheader}
                  size={BodyTextSize.SMALL}
                  color={colors.textSecondary}
                />
              )}
            </TitleTextContainer>
            {updateDate && (
              <BodyText
                text={getFriendlyDate({
                  date: utcToZonedTime(parseISO(updateDate + 'Z'), timeZone),
                  intl,
                  relativeRecent: true,
                })}
                size={BodyTextSize.SMALL}
                color={colors.textSecondary}
              />
            )}
            {useToggleSwitch && (
              <ToggleContainer>
                <BodyText
                  testID={tID('MessengerUtilityItem-bodyText')}
                  text={
                    isToggleSwitchOn
                      ? formatMessage({
                          defaultMessage: 'Inactive',
                          description: 'Label for disabled toggle switch',
                        })
                      : formatMessage({
                          defaultMessage: 'Active',
                          description: 'Label for enabled toggle switch',
                        })
                  }
                  size={BodyTextSize.SMALL}
                  color={colors.textSecondary}
                />
                <ToggleSwitch
                  isOn={isToggleSwitchOn}
                  onPress={() => {
                    onToggleSwitch && onToggleSwitch(!isToggleSwitchOn)
                    setIsToggleSwitchOn(!isToggleSwitchOn)
                  }}
                />
              </ToggleContainer>
            )}
          </TitleContainer>
          <TextContainer testID={tID('MessengerUtilityItem-textContainer')} isExpanded={isExpanded}>
            {useTokenTextInput && hasRichTextTokens ? (
              <TokenTextInput
                initialValue={text}
                onChange={onPress}
                readonly
                customStyles={{
                  backgroundColor: isHovered ? colors.tertiaryButtonBackgroundHover : '',
                  color: colors.textSecondary,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              />
            ) : (
              <BodyTextContainer>
                <BodyText
                  text={convertHtmlSpace(parseHtmlTags(convertHTMLParagraphBreaks(text)))}
                  size={BodyTextSize.SMALL}
                  color={colors.textSecondary}
                  numberOfLines={!isExpanded ? 3 : undefined}
                />
              </BodyTextContainer>
            )}
          </TextContainer>
          {isExpanded && expandedContent && <ExpandedContentContainer>{expandedContent}</ExpandedContentContainer>}
        </InnerContainer>
      )}
    </MessengerUtilityItemButton>
  )
}
